/**
 * Navigation active-state helpers for locale-prefixed routes.
 */

import type { DropdownType } from '@/types/header';

export function normalizePath(path: string): string {
  const p = path.replace(/\/$/, '');
  return p || '/';
}

/** True when current route is exactly `href` or nested under it (e.g. /en/foo/bar matches /en/foo). */
export function isPathActive(pathname: string, href: string): boolean {
  if (!href || href === '#' || href === '#!' || href.startsWith('#')) return false;
  if (href.startsWith('http://') || href.startsWith('https://')) return false;

  const path = normalizePath(pathname);
  const link = normalizePath(href);

  if (path === link) return true;
  if (link !== '/' && path.startsWith(`${link}/`)) return true;
  return false;
}

/** Same rules as HomeBuilderNav.localizeDropdownLink — returns `#` for non-links. */
export function buildLocalizedNavHref(raw: string | null | undefined, locale: string): string {
  const url = (raw ?? '').trim();
  if (!url || url === '#!' || url.startsWith('#')) return url || '#';
  if (/^(https?:|mailto:|tel:|javascript:)/i.test(url)) return url;

  const path = url.startsWith('/') ? url : `/${url}`;
  const localePrefix = `/${locale}`;
  if (path === localePrefix || path.startsWith(`${localePrefix}/`)) {
    return path;
  }
  return `${localePrefix}${path}`;
}

/** True when a raw nav URL localizes to a real link that matches the current path. */
function isActiveNavUrl(
  pathname: string,
  locale: string,
  rawUrl: string | null | undefined,
): boolean {
  const href = buildLocalizedNavHref(rawUrl, locale);
  return href !== '#' && href !== '#!' && isPathActive(pathname, href);
}

/** Any direct navlink (skipping external ones) matches the current path. */
function hasActiveNavlink(pathname: string, locale: string, item: DropdownType): boolean {
  for (const nl of item.navlinks ?? []) {
    if (nl.isExternal) continue;
    if (isActiveNavUrl(pathname, locale, nl.navUrl)) return true;
  }
  return false;
}

/** Any sub-dropdown entry (skipping external ones) matches the current path. */
function hasActiveSubDropdown(pathname: string, locale: string, item: DropdownType): boolean {
  for (const sd of item.subDropDown ?? []) {
    for (const sub of sd.subDropdown ?? []) {
      if (sub.isExternal) continue;
      if (isActiveNavUrl(pathname, locale, sub.navUrl)) return true;
    }
  }
  return false;
}

/** Parent link or any descendant nav URL matches current path. */
export function isDropdownNavActive(pathname: string, locale: string, item: DropdownType): boolean {
  if (isActiveNavUrl(pathname, locale, item.dropdownLink)) return true;
  if (hasActiveNavlink(pathname, locale, item)) return true;
  if (hasActiveSubDropdown(pathname, locale, item)) return true;
  return false;
}
